package com.havn.app.features.leads.presentation

import com.havn.app.core.logging.HavnLogger
import com.havn.app.features.auth.domain.AuthRepository
import com.havn.app.features.auth.domain.AuthState
import com.havn.app.features.leads.domain.Lead
import com.havn.app.features.leads.domain.LeadPropertySource
import com.havn.app.features.leads.domain.LeadRepository
import com.havn.app.features.openhouses.domain.LeadTemperature
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch

class LeadsViewModel(
    private val leadRepository: LeadRepository,
    private val authRepository: AuthRepository,
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
) {
    private val _uiState = MutableStateFlow(LeadsUiState())
    val uiState: StateFlow<LeadsUiState> = _uiState.asStateFlow()

    private var agentId: String? = null
    private var selectedAgencyId: String? = null
    private var hasLoadedAgencyContext = false
    private val temperatureUpdateJobs = mutableMapOf<String, Job>()
    private val confirmedTemperatures = mutableMapOf<String, LeadTemperature>()

    init {
        scope.launch {
            authRepository.authState.collect { state ->
                val nextAgentId = (state as? AuthState.LoggedIn)?.user?.id
                agentId = nextAgentId
                if (nextAgentId == null) {
                    hasLoadedAgencyContext = false
                    temperatureUpdateJobs.values.forEach { it.cancel() }
                    temperatureUpdateJobs.clear()
                    confirmedTemperatures.clear()
                    _uiState.value = LeadsUiState()
                } else if (hasLoadedAgencyContext) {
                    loadLeads(nextAgentId)
                }
            }
        }
    }

    fun refresh() {
        val currentAgentId = agentId ?: return
        scope.launch { loadLeads(currentAgentId) }
    }

    fun setAgencyContext(agencyId: String?) {
        if (hasLoadedAgencyContext && selectedAgencyId == agencyId) return
        selectedAgencyId = agencyId
        hasLoadedAgencyContext = true
        val currentAgentId = agentId ?: return
        scope.launch { loadLeads(currentAgentId) }
    }

    fun updateSearchQuery(query: String) {
        if (_uiState.value.searchQuery == query) return
        _uiState.value = _uiState.value.copy(searchQuery = query, error = null)
        val currentAgentId = agentId ?: return
        scope.launch { loadLeads(currentAgentId) }
    }

    fun loadMoreLeads() {
        val currentAgentId = agentId ?: return
        val state = _uiState.value
        if (state.isLoading || state.isLoadingMore || !state.hasMoreLeads) return
        scope.launch { loadLeads(currentAgentId, append = true) }
    }

    fun selectPropertyFilter(propertyId: String?) {
        _uiState.value = _uiState.value.copy(selectedPropertyId = propertyId, error = null)
    }

    fun selectTemperatureFilter(temperature: LeadTemperature?) {
        _uiState.value = _uiState.value.copy(
            temperatureFilter = temperature,
            followUpFilter = false,
            error = null
        )
    }

    fun selectFollowUpFilter() {
        _uiState.value = _uiState.value.copy(
            temperatureFilter = null,
            followUpFilter = true,
            error = null
        )
    }

    fun openLead(leadId: String) {
        val currentAgentId = agentId ?: return
        val lead = _uiState.value.leads.firstOrNull { it.id == leadId }
        _uiState.value = _uiState.value.copy(
            mode = LeadsMode.Detail,
            selectedLeadId = leadId,
            isDetailLoading = true,
            notesDraft = lead?.notes.orEmpty(),
            error = null
        )
        scope.launch {
            leadRepository.leadDetail(currentAgentId, leadId)
                .onSuccess { freshLead ->
                    replaceLead(freshLead)
                    _uiState.value = _uiState.value.copy(
                        isDetailLoading = false,
                        notesDraft = freshLead.notes.orEmpty()
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isDetailLoading = false)
                    setError(it)
                }
        }
    }

    fun closeDetail() {
        _uiState.value = _uiState.value.copy(
            mode = LeadsMode.List,
            selectedLeadId = null,
            notesDraft = "",
            error = null
        )
    }

    fun updateNotesDraft(notes: String) {
        _uiState.value = _uiState.value.copy(notesDraft = notes, error = null)
    }

    fun saveNotes() {
        val currentAgentId = agentId ?: return
        val leadId = _uiState.value.selectedLeadId ?: return
        val notes = _uiState.value.notesDraft
        _uiState.value = _uiState.value.copy(isSaving = true, error = null)
        scope.launch {
            leadRepository.updateLeadNotes(currentAgentId, leadId, notes)
                .onSuccess { lead ->
                    replaceLead(lead)
                    _uiState.value = _uiState.value.copy(
                        isSaving = false,
                        notesDraft = lead.notes.orEmpty()
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    fun setTemperature(temperature: LeadTemperature) {
        val currentAgentId = agentId ?: return
        val leadId = _uiState.value.selectedLeadId ?: return
        val previousLead = _uiState.value.leads.firstOrNull { it.id == leadId } ?: return
        if (previousLead.temperature == temperature) return
        val confirmedTemperature = confirmedTemperatures[leadId] ?: previousLead.temperature

        replaceLead(previousLead.copy(temperature = temperature), markConfirmed = false)
        _uiState.value = _uiState.value.copy(error = null)

        temperatureUpdateJobs.remove(leadId)?.cancel()
        lateinit var updateJob: Job
        updateJob = scope.launch {
            delay(TEMPERATURE_UPDATE_DEBOUNCE_MS)
            leadRepository.updateLeadTemperature(currentAgentId, leadId, temperature)
                .onSuccess {
                    if (temperatureUpdateJobs[leadId] === updateJob) {
                        temperatureUpdateJobs.remove(leadId)
                        confirmedTemperatures[leadId] = temperature
                    }
                }
                .onFailure {
                    if (temperatureUpdateJobs[leadId] === updateJob && isActive) {
                        temperatureUpdateJobs.remove(leadId)
                        replaceLead(previousLead.copy(temperature = confirmedTemperature), markConfirmed = false)
                        setError(it)
                    }
                }
        }
        temperatureUpdateJobs[leadId] = updateJob
    }

    fun clearError() {
        _uiState.value = _uiState.value.copy(error = null)
    }

    fun dispose() {
        temperatureUpdateJobs.values.forEach { it.cancel() }
        temperatureUpdateJobs.clear()
        scope.cancel()
    }

    private suspend fun loadLeads(currentAgentId: String, append: Boolean = false) {
        val state = _uiState.value
        val offset = if (append) state.leads.size else 0
        _uiState.value = if (append) {
            state.copy(isLoadingMore = true, error = null)
        } else {
            state.copy(isLoading = true, error = null)
        }
        leadRepository.leadPage(
            agentId = currentAgentId,
            agencyId = selectedAgencyId,
            query = _uiState.value.searchQuery,
            offset = offset,
            limit = LEAD_PAGE_SIZE
        )
            .onSuccess { leads ->
                val merged = if (append) (_uiState.value.leads + leads).distinctBy { it.id } else leads
                merged.forEach { lead -> confirmedTemperatures[lead.id] = lead.temperature }
                val selectedId = _uiState.value.selectedLeadId?.takeIf { selectedId ->
                    merged.any { it.id == selectedId }
                }
                _uiState.value = _uiState.value.copy(
                    isLoading = false,
                    isLoadingMore = false,
                    leads = merged,
                    hasMoreLeads = leads.size == LEAD_PAGE_SIZE,
                    selectedLeadId = selectedId,
                    mode = if (selectedId == null) LeadsMode.List else _uiState.value.mode
                )
            }
            .onFailure {
                _uiState.value = _uiState.value.copy(isLoading = false, isLoadingMore = false)
                setError(it)
            }
    }

    private fun replaceLead(lead: Lead, markConfirmed: Boolean = true) {
        val existing = _uiState.value.leads
        val updated = if (existing.any { it.id == lead.id }) {
            existing.map { if (it.id == lead.id) lead else it }
        } else {
            listOf(lead) + existing
        }
        if (markConfirmed) {
            confirmedTemperatures[lead.id] = lead.temperature
        }
        _uiState.value = _uiState.value.copy(leads = updated)
    }

    private fun setError(error: Throwable) {
        HavnLogger.error("LeadsViewModel", "User-visible leads error", error)
        _uiState.value = _uiState.value.copy(
            error = error.message?.takeIf { it.isNotBlank() } ?: "Something went wrong while loading leads."
        )
    }
}

data class LeadsUiState(
    val isLoading: Boolean = false,
    val isLoadingMore: Boolean = false,
    val isDetailLoading: Boolean = false,
    val isSaving: Boolean = false,
    val error: String? = null,
    val leads: List<Lead> = emptyList(),
    val hasMoreLeads: Boolean = false,
    val selectedLeadId: String? = null,
    val mode: LeadsMode = LeadsMode.List,
    val searchQuery: String = "",
    val selectedPropertyId: String? = null,
    val temperatureFilter: LeadTemperature? = null,
    val followUpFilter: Boolean = false,
    val notesDraft: String = ""
) {
    val selectedLead: Lead?
        get() = leads.firstOrNull { it.id == selectedLeadId }

    val propertyFilters: List<LeadPropertySource>
        get() = leads
            .map { it.sourceProperty }
            .distinctBy { it.id }
            .sortedBy { it.title }

    val filteredLeads: List<Lead>
        get() = leads
            .filter { selectedPropertyId == null || it.propertyId == selectedPropertyId }
            .filter { temperatureFilter == null || it.temperature == temperatureFilter }
            .filter { !followUpFilter || it.hasCompletedFollowUp }
            .filter { lead ->
                val query = searchQuery.trim()
                if (query.isBlank()) {
                    true
                } else {
                    lead.searchText.matchesTokenizedQuery(query)
                }
            }
            .sortedWith(
                compareByDescending<Lead> { it.createdAt.orEmpty() }
                    .thenByDescending { it.updatedAt.orEmpty() }
                    .thenByDescending { it.checkedInAt }
                    .thenByDescending { it.id }
            )

    val hotCount: Int
        get() = leads.count { it.temperature == LeadTemperature.Hot }

    val warmCount: Int
        get() = leads.count { it.temperature == LeadTemperature.Warm }

    val coldCount: Int
        get() = leads.count { it.temperature == LeadTemperature.Cold }

    val followUpCount: Int
        get() = leads.sumOf { lead -> lead.followUps.count { it.status == "completed" } }
}

private const val LEAD_PAGE_SIZE = 50
private const val TEMPERATURE_UPDATE_DEBOUNCE_MS = 250L

enum class LeadsMode {
    List,
    Detail
}

private val Lead.searchText: String
    get() = listOfNotNull(fullName, phone, email, sourceProperty.title, sourceProperty.address)
        .joinToString(" ")

private fun String.matchesTokenizedQuery(query: String): Boolean {
    val haystack = lowercase()
    return query
        .trim()
        .lowercase()
        .split(Regex("\\s+"))
        .filter { it.isNotBlank() }
        .all { token -> haystack.contains(token) }
}

private val Lead.hasCompletedFollowUp: Boolean
    get() = followUps.any { it.status == "completed" }
